import { ChangeEvent, forwardRef, memo, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import './index.scss'
import { useTranslation } from 'react-i18next'
import Button from '../Button'
import Icon from '../Icon'
import { ICON } from '../Icon/data'
import { fileSizeToMb } from '../../services/utils/convert'
import { COLOR } from '../../data/variables'
import useDynamicRef from '../../hooks/useDynamicRef'
import { isValidUrl } from '../../services/utils/validation'
import { extractFileName } from '../../services/utils/misc'

type Attachment = {
  attachment?: string
  filename?: string
  id?: number
}

type FileUpload = {
  label: string
  text?: string
  maxSize?: number
  maxFiles?: number
  allowedFiles?: 'all' | 'image'
  value?: Attachment[] | string[]
}

const ALLOWED_FILE_TYPE = {
  all: [],
  image: ['jpg', 'png']
}

const FileUpload = memo(forwardRef(({
  label, text, maxSize = 20, maxFiles = 5, allowedFiles = 'all', value
} : FileUpload, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const { t } = useTranslation()

  const defaultValue = Array.isArray(value) ? value : [value]
  const [files, setFiles] = useState<File[] | Attachment[]>(value ? defaultValue : [])

  const acceptedFileTypes = useMemo(() => ({
    all: ['image/*'],
    image: ['image/*']
  }), [allowedFiles])

  const infoText = useMemo(() => text ??
    `${t('Max file size is {{size}} MB. Supported file types are {{type}}. You can upload a maximum of {{amount}} files.', {
      size: maxSize,
      type: ALLOWED_FILE_TYPE[allowedFiles].join(', '),
      amount: maxFiles
    })}`, [t, text, maxSize, maxFiles, allowedFiles])

  const onButtonClick = () => {
    inputRef?.current.click()
  }

  const handleUpload = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = [...files, ...Array.from(e.target.files)]
      setFiles(newFiles);
    }
  }, [files])

  const removeFile = useCallback((index: number) => {
    const tempFiles = [...files]
    tempFiles.splice(index, 1)
    setFiles(tempFiles)
  }, [files])

  const validateFile = useCallback((idx: number) => {
    let hasWarning = false
    const warnings: string[] = []
    const { size } = files[idx]
    const mb = fileSizeToMb(size)

    if (mb > maxSize) warnings.push(`${t('File exceeds size limit')} (${mb.toFixed(1)} MB).`)
    if (idx + 1 > maxFiles) {
      warnings.push(t('You can only upload {{max}} files. You currently have {{count}} files selected.', {
        max: maxFiles, count: files.length
      }))
    }

    if (warnings.length) hasWarning = true

    return { hasWarning, warnings }
  }, [maxFiles, maxSize, files])

  const getFiles = useCallback(() => {
    return files.filter((file, idx) => !validateFile(idx).hasWarning)
  }, [validateFile, files])

  const forwardRefAction = useDynamicRef({
    getFiles
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  return (
    <div className="FileUpload">
      <div className="FileUpload__label">{label}</div>
      <div className="FileUpload__text">{infoText}</div>
      <input
        ref={inputRef}
        type="file"
        id="file"
        style={{ display: 'none' }}
        multiple={maxFiles > 1}
        onChange={(e) => handleUpload(e)}
        accept={acceptedFileTypes[allowedFiles].join(', ')}
      />
      <Button
        label={t('Upload')}
        type="secondary"
        size="m"
        onClick={() => onButtonClick()}
      />
      <div className="FileUpload__files">
        {files?.map((file, idx) => {
          let { name, filename, attachment } = file
          if (typeof file === 'string') {
            name = extractFileName(file)
            attachment = file
            filename = name
          }
          const { hasWarning, warnings } = validateFile(idx)
          let label = name
          if (filename && isValidUrl(attachment)) {
            label = (<a href={attachment} download={filename} target="_blank">{filename}</a>)
          }
          return (
            <div className="FileUpload__files-file">
              <div className="FileUpload__files-file__info">
                <div className="FileUpload__files-file__name">{label}</div>
                <div className="FileUpload__files-file__icons">
                  {hasWarning && <Icon icon={ICON.WARNING} color={COLOR.ERROR_RED} />}
                  <div
                    className="FileUpload__files-file__button"
                    role="button"
                    tabIndex={0}
                    onClick={() => removeFile(idx)}
                  >
                    <Icon icon={ICON.CLOSE} />
                  </div>
                </div>
              </div>
              {hasWarning && (
                <div className="FileUpload__files-file__error">
                  {warnings.map((warning) => (
                    <span>{warning}</span>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}))

export default FileUpload
